import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X, Sparkles } from "lucide-react";

const GLASS_PALETTES = {
  clean: ["rgba(255,255,255,.72)", "rgba(255,255,255,.18)", "rgba(32,247,220,.10)"],
  frost: ["rgba(255,255,255,.82)", "rgba(226,232,240,.24)", "rgba(255,255,255,.08)"],
  aqua:  ["rgba(32,247,220,.48)",  "rgba(255,255,255,.18)", "rgba(156,255,59,.16)"],
  dark:  ["rgba(15,23,42,.70)",    "rgba(255,255,255,.12)", "rgba(0,0,0,.34)"],
  glow:  ["rgba(32,247,220,.40)",  "rgba(255,255,255,.18)", "rgba(139,61,255,.22)"],
};
const FILL_SWATCHES = ["#27E6D1","#B9FF2F","#8b3dff","#ff4e84","#ff9c36","#ffffff","#0db7ff","#001713","#5c3a18","#22c55e","#f97316","transparent"];

/**
 * Shape popover host. `kind` ∈ glass | fill | stroke | radius | blur | properties.
 * `anchorRect` is the bounding rect of the toolbar button that triggered it.
 */
export default function ShapePopover({ kind, element, anchorRect, onUpdate, onAction, onClose }) {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });

  useLayoutEffect(() => {
    if (!ref.current || !anchorRect) return;
    const w = ref.current.offsetWidth || 380;
    const h = ref.current.offsetHeight || 320;
    let left = anchorRect.left + anchorRect.width / 2 - w / 2;
    left = Math.max(12, Math.min(left, window.innerWidth - w - 12));
    let top = anchorRect.bottom + 10;
    if (top + h + 12 > window.innerHeight) top = Math.max(12, anchorRect.top - h - 10);
    setPos({ left, top });
  }, [anchorRect, kind]);

  useEffect(() => {
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    const onScroll = () => onClose();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("resize", onScroll);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("resize", onScroll);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      data-testid={`md-shape-pop-${kind}`}
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "fixed", left: pos.left, top: pos.top, zIndex: 2700,
        width: "min(412px, calc(100vw - 26px))",
        background: "var(--pl-surface)",
        border: "1px solid var(--pl-border)",
        boxShadow: "0 24px 70px rgba(0,0,0,0.42)",
        borderRadius: 18, color: "var(--pl-text)", overflow: "hidden",
      }}
      className="backdrop-blur"
    >
      <Head kind={kind} onClose={onClose} />
      <div style={{ padding: 14, display: "grid", gap: 13 }}>
        {kind === "glass"      && <GlassBody      element={element} onUpdate={onUpdate} />}
        {kind === "fill"       && <FillBody       element={element} onUpdate={onUpdate} />}
        {kind === "stroke"     && <StrokeBody     element={element} onUpdate={onUpdate} />}
        {kind === "radius"     && <RadiusBody     element={element} onUpdate={onUpdate} onClose={onClose} />}
        {kind === "blur"       && <BlurBody       element={element} onUpdate={onUpdate} />}
        {kind === "properties" && <PropertiesBody element={element} onUpdate={onUpdate} onAction={onAction} />}
      </div>
    </div>
  );
}

function Head({ kind, onClose }) {
  const meta = {
    glass:      { title: "Glass Style",       sub: "One-click blur, transparency, and glassmorphism" },
    fill:       { title: "Fill",              sub: "Pick a solid color, gradient, or transparency" },
    stroke:     { title: "Stroke",            sub: "Outline color and weight" },
    radius:     { title: "Corner Radius",     sub: "Round the corners or tune the shape into a pill" },
    blur:       { title: "Blur",              sub: "Blur the shape itself or create a frosted background blur" },
    properties: { title: "Shape Properties",  sub: "Fast controls for size, appearance, and layer styling" },
  }[kind] || { title: "", sub: "" };
  return (
    <div style={{
      padding: "13px 14px",
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) 34px",
      gap: 12, alignItems: "center",
      borderBottom: "1px solid var(--pl-border)",
    }}>
      <div>
        <strong style={{ display: "block", fontSize: 15, letterSpacing: "-.02em" }}>{meta.title}</strong>
        <span style={{ display: "block", marginTop: 3, color: "var(--pl-text-muted)", fontSize: 12, fontWeight: 600 }}>{meta.sub}</span>
      </div>
      <button
        onClick={onClose}
        data-testid="md-shape-pop-close"
        className="grid place-items-center rounded-lg text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] hover:bg-white/[0.06]"
        style={{ width: 32, height: 32 }}
        aria-label="Close popover"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ============ Glass ============ */
function GlassBody({ element, onUpdate }) {
  const [tab, setTab] = useState("style");
  const [preset, setPreset] = useState(element._glassPreset || "clean");
  const [blur, setBlur]   = useState(element._glassBlur ?? 32);
  const [bgBlur, setBgBlur] = useState(element._glassBgBlur ?? 18);
  const [opacity, setOpacity] = useState(element._glassOpacity ?? 68);
  const [lightBorder, setLightBorder] = useState(element._glassLightBorder ?? 1);
  const [shadow, setShadow] = useState(element._glassShadow ?? 24);
  const radius = parseInt(element.style?.borderRadius, 10) || 24;
  const [r, setR] = useState(radius);

  const apply = (next = {}) => {
    const cfg = { preset, blur, bgBlur, opacity, lightBorder, shadow, r, ...next };
    const p = GLASS_PALETTES[cfg.preset] || GLASS_PALETTES.clean;
    onUpdate({
      _glassPreset: cfg.preset, _glassBlur: cfg.blur, _glassBgBlur: cfg.bgBlur,
      _glassOpacity: cfg.opacity, _glassLightBorder: cfg.lightBorder, _glassShadow: cfg.shadow,
      style: {
        background: `linear-gradient(135deg, ${p[0]}, ${p[1]} 46%, ${p[2]})`,
        border: `${cfg.lightBorder}px solid rgba(255,255,255,.55)`,
        borderRadius: cfg.r === 999 ? 999 : cfg.r,
        opacity: cfg.opacity / 100,
        backdropFilter: `blur(${cfg.bgBlur}px) saturate(145%)`,
        WebkitBackdropFilter: `blur(${cfg.bgBlur}px) saturate(145%)`,
        boxShadow: cfg.preset === "glow"
          ? `0 0 46px rgba(32,247,220,.32), 0 ${Math.round(cfg.shadow/2)}px ${cfg.shadow}px rgba(0,0,0,.30), inset 0 0 ${cfg.blur}px rgba(255,255,255,.12)`
          : `0 ${Math.round(cfg.shadow/2)}px ${cfg.shadow}px rgba(0,0,0,.30), inset 0 0 ${cfg.blur}px rgba(255,255,255,.10)`,
      },
    });
  };
  // apply once on mount
  useEffect(() => { apply(); /* eslint-disable-next-line */ }, []);

  return (
    <>
      <Tabs value={tab} onChange={setTab} items={[{id:"style",label:"Style"},{id:"advanced",label:"Advanced"}]} />
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {["clean","frost","aqua","dark","glow"].map((p) => (
          <Chip
            key={p}
            active={preset === p}
            data-testid={`md-glass-preset-${p}`}
            onClick={() => { setPreset(p); apply({ preset: p }); }}
          >
            {p[0].toUpperCase() + p.slice(1)}
          </Chip>
        ))}
      </div>
      <Slider label="Blur" min={0} max={64} value={blur} suffix="px" onChange={(v) => { setBlur(v); apply({ blur: v }); }} testId="md-glass-blur" />
      <Slider label="Background blur" min={0} max={48} value={bgBlur} suffix="px" onChange={(v) => { setBgBlur(v); apply({ bgBlur: v }); }} />
      <Slider label="Opacity" min={10} max={100} value={opacity} suffix="%" onChange={(v) => { setOpacity(v); apply({ opacity: v }); }} />
      {tab === "advanced" && (
        <>
          <Slider label="Light border" min={0} max={4} value={lightBorder} suffix="px" onChange={(v) => { setLightBorder(v); apply({ lightBorder: v }); }} />
          <Slider label="Shadow softness" min={0} max={80} value={shadow} suffix="px" onChange={(v) => { setShadow(v); apply({ shadow: v }); }} />
          <Slider label="Corner radius" min={0} max={110} value={r} suffix="px" onChange={(v) => { setR(v); apply({ r: v }); }} />
        </>
      )}
      <PreviewChip onReset={() => onUpdate({ style: { background: "rgba(255,255,255,.05)", backdropFilter: "", WebkitBackdropFilter: "", boxShadow: "" } })} />
    </>
  );
}

/* ============ Fill ============ */
function FillBody({ element, onUpdate }) {
  return (
    <>
      <SectionLabel>Solid fill</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6 }}>
        {FILL_SWATCHES.map((c) => (
          <button
            key={c}
            data-testid={`md-fill-${c}`}
            onClick={() => onUpdate({ style: { background: c } })}
            title={c}
            style={{
              height: 34, borderRadius: 11, cursor: "pointer",
              border: "1px solid var(--pl-border)",
              background: c === "transparent"
                ? "repeating-conic-gradient(#999 0% 25%, #fff 25% 50%) 50% / 12px 12px"
                : c,
            }}
          />
        ))}
      </div>
      <SectionLabel>Fill mode</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        <BtnOpt onClick={() => onUpdate({ style: { background: "transparent" } })}>None</BtnOpt>
        <BtnOpt onClick={() => onUpdate({ style: { background: "#d4b14b" } })}>Solid</BtnOpt>
        <BtnOpt onClick={() => onUpdate({ style: { background: "linear-gradient(135deg,#27E6D1,#B9FF2F)" } })}>Gradient</BtnOpt>
      </div>
    </>
  );
}

/* ============ Stroke ============ */
function StrokeBody({ element, onUpdate }) {
  const [w, setW] = useState(parseInt(element.style?.borderWidth, 10) || 1);
  const [s, setS] = useState(element.style?.borderStyle || "solid");
  return (
    <>
      <SectionLabel>Stroke color</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6 }}>
        {FILL_SWATCHES.map((c) => (
          <button
            key={c}
            data-testid={`md-stroke-${c}`}
            onClick={() => onUpdate({ style: { borderColor: c === "transparent" ? "transparent" : c, borderStyle: s, borderWidth: w } })}
            style={{ height: 34, borderRadius: 11, cursor: "pointer", border: "1px solid var(--pl-border)",
              background: c === "transparent" ? "repeating-conic-gradient(#999 0% 25%, #fff 25% 50%) 50% / 12px 12px" : c }}
          />
        ))}
      </div>
      <Slider label="Width" min={0} max={12} value={w} suffix="px"
        onChange={(v) => { setW(v); onUpdate({ style: { borderWidth: v, borderStyle: s } }); }}
        testId="md-stroke-width" />
      <SectionLabel>Style</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        {["solid","dashed","dotted"].map((st) => (
          <BtnOpt key={st} active={s === st} onClick={() => { setS(st); onUpdate({ style: { borderStyle: st, borderWidth: w } }); }}>
            <span style={{ display:"inline-block", width: 44, height: 0, borderTop: `3px ${st} currentColor`, opacity: 0.9 }} />
          </BtnOpt>
        ))}
      </div>
    </>
  );
}

/* ============ Radius ============ */
function RadiusBody({ element, onUpdate, onClose }) {
  const cur = (() => {
    const r = element.style?.borderRadius;
    if (r === 999 || r === "999px") return 999;
    return parseInt(r, 10) || 0;
  })();
  const [r, setR] = useState(cur);
  return (
    <>
      <Slider label="All corners" min={0} max={110} value={r === 999 ? 110 : r} suffix={r === 999 ? "Pill" : "px"}
        onChange={(v) => { setR(v); onUpdate({ style: { borderRadius: v } }); }} testId="md-radius-range" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        {[0, 12, 24, 44, 70, 999].map((p) => (
          <BtnOpt
            key={p}
            active={r === p}
            data-testid={`md-radius-${p}`}
            onClick={() => { setR(p); onUpdate({ style: { borderRadius: p } }); onClose(); }}
          >
            {p === 999 ? "Pill" : `${p} px`}
          </BtnOpt>
        ))}
      </div>
    </>
  );
}

/* ============ Blur ============ */
function BlurBody({ element, onUpdate }) {
  const [shapeBlur, setShapeBlur] = useState(parseInt(element.style?.filter?.match?.(/blur\((\d+)px\)/)?.[1] || "0", 10));
  const [bgBlur, setBgBlur] = useState(parseInt(element.style?.backdropFilter?.match?.(/blur\((\d+)px\)/)?.[1] || "0", 10));
  return (
    <>
      <Slider label="Shape blur" min={0} max={32} value={shapeBlur} suffix="px"
        onChange={(v) => { setShapeBlur(v); onUpdate({ style: { filter: v ? `blur(${v}px)` : "" } }); }} />
      <Slider label="Background blur" min={0} max={48} value={bgBlur} suffix="px"
        onChange={(v) => { setBgBlur(v); onUpdate({ style: { backdropFilter: v ? `blur(${v}px) saturate(145%)` : "", WebkitBackdropFilter: v ? `blur(${v}px) saturate(145%)` : "" } }); }} />
      <BtnWide primary onClick={() => onUpdate({
        style: {
          backdropFilter: "blur(18px) saturate(145%)",
          WebkitBackdropFilter: "blur(18px) saturate(145%)",
          background: "rgba(255,255,255,0.18)",
          border: "1px solid rgba(255,255,255,0.55)",
        },
      })}>
        Apply frosted blur
      </BtnWide>
      <BtnWide onClick={() => { setShapeBlur(0); setBgBlur(0); onUpdate({ style: { filter: "", backdropFilter: "", WebkitBackdropFilter: "" } }); }}>
        Clear blur
      </BtnWide>
    </>
  );
}

/* ============ Properties ============ */
function PropertiesBody({ element, onUpdate, onAction }) {
  const [w, setW] = useState(element.style?.width || 200);
  const [h, setH] = useState(element.style?.height || 120);
  const [op, setOp] = useState(Math.round((Number(element.style?.opacity ?? 1)) * 100));
  return (
    <>
      <Slider label="Width" min={80} max={760} value={w} suffix="px" onChange={(v) => { setW(v); onUpdate({ style: { width: v } }); }} />
      <Slider label="Height" min={40} max={420} value={h} suffix="px" onChange={(v) => { setH(v); onUpdate({ style: { height: v } }); }} />
      <Slider label="Opacity" min={10} max={100} value={op} suffix="%" onChange={(v) => { setOp(v); onUpdate({ style: { opacity: v/100 } }); }} />
      <BtnWide primary onClick={() => onAction?.("open-glass")}>
        <Sparkles className="h-4 w-4" /> Turn into Glass
      </BtnWide>
    </>
  );
}

/* ============ Atoms ============ */
function Tabs({ value, onChange, items }) {
  return (
    <div style={{ display: "flex", gap: 8 }}>
      {items.map((i) => (
        <button
          key={i.id}
          data-testid={`md-shape-tab-${i.id}`}
          onClick={() => onChange(i.id)}
          className={`rounded-full font-bold text-[12px] px-3 h-8 ${value===i.id?"text-[var(--pl-aqua)]":"text-[var(--pl-text)]"}`}
          style={{
            border: "1px solid " + (value===i.id ? "rgba(32,247,220,0.42)" : "var(--pl-border)"),
            background: value===i.id ? "rgba(32,247,220,0.12)" : "rgba(255,255,255,0.06)",
            cursor: "pointer",
          }}
        >
          {i.label}
        </button>
      ))}
    </div>
  );
}

function Chip({ active, children, onClick, "data-testid": testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`rounded-full font-bold text-[12px] px-3 h-8 ${active ? "text-[var(--pl-aqua)]" : "text-[var(--pl-text)]"}`}
      style={{
        border: "1px solid " + (active ? "rgba(32,247,220,0.42)" : "var(--pl-border)"),
        background: active ? "rgba(32,247,220,0.12)" : "rgba(255,255,255,0.06)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function Slider({ label, min, max, value, suffix, onChange, testId }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "118px minmax(0,1fr) 60px", gap: 10, alignItems: "center", minHeight: 32, fontSize: 12, fontWeight: 700 }}>
      <label style={{ color: "var(--pl-text-muted)" }}>{label}</label>
      <input
        data-testid={testId}
        type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: "var(--pl-aqua)" }}
      />
      <b style={{ textAlign: "right", fontSize: 11, color: "var(--pl-text)" }}>
        {suffix === "Pill" ? "Pill" : `${value} ${suffix}`}
      </b>
    </div>
  );
}

function BtnOpt({ active, children, onClick, "data-testid": testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      style={{
        minHeight: 40, borderRadius: 12,
        background: active ? "rgba(32,247,220,0.12)" : "rgba(255,255,255,0.06)",
        border: "1px solid " + (active ? "rgba(32,247,220,0.42)" : "var(--pl-border)"),
        color: active ? "var(--pl-aqua)" : "var(--pl-text)",
        fontWeight: 800, fontSize: 12, cursor: "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      }}
    >
      {children}
    </button>
  );
}

function BtnWide({ primary, children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", minHeight: 42, borderRadius: 12,
        color: primary ? "#001b18" : "var(--pl-text)",
        background: primary ? "linear-gradient(135deg, var(--pl-aqua), var(--pl-lime, #B9FF2F))" : "rgba(255,255,255,0.06)",
        border: primary ? "0" : "1px solid var(--pl-border)",
        fontWeight: 900, fontSize: 13, cursor: "pointer",
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      }}
    >
      {children}
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--pl-aqua)" }}>
      {children}
    </div>
  );
}

function PreviewChip({ onReset }) {
  return (
    <div style={{
      border: "1px solid var(--pl-border)",
      background: "rgba(255,255,255,0.06)",
      borderRadius: 14, padding: 10,
      display: "grid", gridTemplateColumns: "54px minmax(0,1fr) auto", gap: 10, alignItems: "center",
    }}>
      <span style={{
        width: 50, height: 50, borderRadius: 13,
        background: "linear-gradient(135deg, rgba(255,255,255,.72), rgba(32,247,220,.18))",
        border: "1px solid rgba(255,255,255,.42)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }} />
      <span>
        <strong style={{ display: "block", fontWeight: 900, fontSize: 13 }}>Glass preview</strong>
        <span style={{ color: "var(--pl-text-muted)", fontSize: 11, fontWeight: 600 }}>
          Background blur + transparency creates a frosted glass effect.
        </span>
      </span>
      <BtnOpt onClick={onReset} data-testid="md-glass-reset">Reset</BtnOpt>
    </div>
  );
}
